/**
 * Email (prompt 0.6.0): the merge tokens and the renderer, the Email timing settings, the built-in
 * templates (the app's own purposes and the v4 codes, English and Spanish), and the link kinds.
 *
 * Nothing here sends anything. Every email is queued by a person's press (or a tenant's press on their
 * own link), waits in the cancel window, and only then leaves through the dispatcher.
 * No dependencies beyond the shared vocabulary and dates, so the app and the API both use it.
 */
import type { vocab } from './vocab';
import { SERVICE_TZ, addDaysToKey, keyToNoonUtc } from './dates';
import { DIRECTOR_WORDING } from './email-wording.gen';
import { composeBody } from './wording';

/* ------------------------------------------------------------------------------------------------
 * Tokens and the renderer
 * ---------------------------------------------------------------------------------------------- */

/** The merge fields the renderer fills, with what each one becomes (the token reference panel). */
export const EMAIL_TOKENS = [
  { token: 'TenantPreferredName', en: "The tenant's preferred name (or first name)", es: 'El nombre preferido del inquilino (o el nombre)' },
  { token: 'UnitNumber', en: 'Apartment', es: 'Apartamento' },
  { token: 'AppointmentDate', en: 'The visit day, written out ("Thursday, October 8")', es: 'El día de la visita, escrito' },
  { token: 'AppointmentTime', en: 'The visit time from the Day plan ("9:30") — staff emails; tenants are told the window', es: 'La hora de la visita según el plan del día (correos al personal)' },
  { token: 'VisitWindow', en: 'The arrival window tenants are told ("between 10:30 and 12:00")', es: 'La franja de llegada que se dice al inquilino ("entre las 10:30 y las 12:00")' },
  { token: 'ServicePeriod', en: 'Morning or afternoon', es: 'Mañana o tarde' },
  { token: 'OfficePhone', en: "The office phone (Admin → Settings)", es: 'El teléfono de la oficina' },
  { token: 'OfficeEmail', en: 'The office email (Admin → Settings → Who gets what)', es: 'El correo de la oficina' },
  { token: 'HoldUntil', en: 'When a hold ends', es: 'Cuándo termina una retención' },
  { token: 'VolunteerName', en: "The volunteer's name", es: 'El nombre del voluntario' },
  { token: 'VolunteerFirst', en: "The volunteer's first name only", es: 'Solo el nombre del voluntario' },
  { token: 'FirstName', en: "The recipient's first name", es: 'El nombre de quien lo recibe' },
  { token: 'StaffName', en: 'The person who pressed Send', es: 'La persona que pulsó Enviar' },
  { token: 'ProgramName', en: 'The program (Housekeeping, Companion & Wellness…)', es: 'El programa' },
  { token: 'BuildingManagerName', en: 'The building manager (Admin → Settings)', es: 'El administrador del edificio' },
  { token: 'TenantName', en: "The tenant's full name (internal emails only)", es: 'El nombre completo del inquilino (solo correos internos)' },
  { token: 'Details', en: 'The report or document the press produced', es: 'El informe o documento que produjo la acción' },
  { token: 'DocumentTitle', en: 'The document sent as a copy', es: 'El documento enviado como copia' },
  { token: 'Message', en: 'A few words typed in Compose', es: 'Unas palabras escritas al redactar' },
  { token: 'Activity', en: 'The outing or errand', es: 'La salida o el mandado' },
  { token: 'Date', en: 'A date (outings, events)', es: 'Una fecha (salidas, eventos)' },
  { token: 'Time', en: 'A time (outings, events)', es: 'Una hora (salidas, eventos)' },
  { token: 'Place', en: 'Where (outings, events)', es: 'Dónde (salidas, eventos)' },
  { token: 'EventName', en: 'The event', es: 'El evento' },
  { token: 'ServicesSelected', en: 'What a volunteer signed up for', es: 'Lo que eligió el voluntario' },
  { token: 'TrainingDate', en: 'The next training date (Settings → Companion)', es: 'La próxima capacitación' },
  { token: 'TrainingTime', en: 'The training time', es: 'La hora de la capacitación' },
  { token: 'TrainerName', en: 'Who leads the training', es: 'Quién dirige la capacitación' },
  { token: 'CompanionNotice', en: 'The Companion & Wellness notice (training and background check) — only when they chose Companion or Wellness walks; empty otherwise (1.8.2)', es: 'El aviso de Acompañamiento y Bienestar (capacitación y verificación de antecedentes): solo si eligieron Acompañamiento o caminatas; vacío si no' },
  { token: 'Month', en: 'The report month', es: 'El mes del informe' },
] as const;

export type EmailToken = (typeof EMAIL_TOKENS)[number]['token'];
export const SUPPORTED_TOKENS: ReadonlySet<string> = new Set(EMAIL_TOKENS.map((t) => t.token));
export type TokenValues = Partial<Record<EmailToken, string | null | undefined>>;

const TOKEN_RE = /\[([A-Za-z][A-Za-z0-9]*)\]/g;

/** Every [Token] a text names, in order, once each. `[?X]` (an already-flagged unknown) is not a token. */
export function tokensIn(text: string): string[] {
  const out: string[] = [];
  for (const m of text.matchAll(TOKEN_RE)) if (!out.includes(m[1]!)) out.push(m[1]!);
  return out;
}

export interface Rendered {
  text: string;
  /** Tokens the renderer does not know: shown as [?Token]; they block Send. */
  unknown: string[];
  /** Known tokens with no value for this message: rendered empty; the preview warns. */
  missing: string[];
}

/** Fill [Tokens]. Unknown ones render as [?Token] (and block Send); known ones without a value render empty. */
export function renderText(template: string, values: TokenValues): Rendered {
  const unknown: string[] = [];
  const missing: string[] = [];
  const text = template.replace(TOKEN_RE, (_all, name: string) => {
    if (!SUPPORTED_TOKENS.has(name)) {
      if (!unknown.includes(name)) unknown.push(name);
      return `[?${name}]`;
    }
    const v = values[name as EmailToken];
    if (v === null || v === undefined || String(v).trim() === '') {
      if (!missing.includes(name)) missing.push(name);
      return '';
    }
    return String(v);
  });
  return { text: text.replace(/[ \t]+([,.])/g, '$1'), unknown, missing };
}

export interface RenderedMessage {
  subject: string;
  body: string;
  unknown: string[];
  missing: string[];
}

export function renderMessage(t: { subject: string; body: string }, values: TokenValues): RenderedMessage {
  const s = renderText(t.subject, values);
  const b = renderText(t.body, values);
  return {
    subject: s.text.replace(/\s+/g, ' ').trim(),
    body: b.text,
    unknown: [...new Set([...s.unknown, ...b.unknown])],
    missing: [...new Set([...s.missing, ...b.missing])],
  };
}

/* ------------------------------------------------------------------------------------------------
 * Dates in words (the tenant's language, the service clock)
 * ---------------------------------------------------------------------------------------------- */

export type Lang = 'en' | 'es';

/** "Thursday, October 8" / "jueves, 8 de octubre" for a civil day. */
export function dayInWords(dayKey: string, lang: Lang): string {
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-US' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(new Date(keyToNoonUtc(dayKey)));
}

export function periodInWords(period: string | null | undefined, lang: Lang): string {
  const p = String(period ?? '');
  if (lang === 'es') return p === 'Afternoon' ? 'por la tarde' : p === 'Evening' ? 'por la noche' : p === 'Morning' ? 'por la mañana' : '';
  return p === 'Afternoon' ? 'in the afternoon' : p === 'Evening' ? 'in the evening' : p === 'Morning' ? 'in the morning' : '';
}

/** A civil day + minutes after midnight on the service clock (America/New_York) → the UTC instant. */
export function serviceInstant(dayKey: string, minutes: number): Date {
  const y = Number(dayKey.slice(0, 4));
  const m = Number(dayKey.slice(4, 6)) - 1;
  const d = Number(dayKey.slice(6, 8));
  const guess = Date.UTC(y, m, d, Math.floor(minutes / 60), minutes % 60);
  const offsetAt = (t: number) => {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: SERVICE_TZ, hourCycle: 'h23', year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' }).formatToParts(new Date(t));
    const g = (k: string) => Number(parts.find((p) => p.type === k)?.value ?? 0);
    return Date.UTC(g('year'), g('month') - 1, g('day'), g('hour'), g('minute')) - t;
  };
  let t = guess - offsetAt(guess);
  t = guess - offsetAt(t);
  return new Date(t);
}

/* ------------------------------------------------------------------------------------------------
 * The Email timing settings (SM_Settings, Category Communications; created when missing)
 * ---------------------------------------------------------------------------------------------- */

export const NOT_SET_VALUE = 'Not set';

export const REMINDER_RULES = ['DAY_BEFORE_0900', 'DAY_BEFORE_1600', 'TWO_DAYS_BEFORE', 'OFF'] as const;
export type ReminderRule = (typeof REMINDER_RULES)[number];
export const DELAY_CHOICES = ['2', '5', '10', '15', '30', '60', 'NEXT_MORNING'] as const;
export const CUTOFF_CHOICES = ['24', '48', 'SAME_DAY_0800'] as const;

export interface EmailSettingDef {
  key: string;
  name: string;
  type: 'Text' | 'Number' | 'Yes/No' | 'Time' | 'Email';
  value: string;
  description: string;
  category: 'Communications' | 'System';
}

export const EMAIL_SETTINGS: readonly EmailSettingDef[] = [
  { key: 'EMAIL_SEND_DELAY_MINUTES', name: 'Cancel window before any email leaves', type: 'Text', value: '5', category: 'Communications', description: "Minutes (2, 5, 10, 15, 30, 60) or NEXT_MORNING (8:00). You press Book or Send; the email waits this long in the Outbox, where you can take it back; then it goes." },
  { key: 'EMAIL_ON_PHONE_BOOKING', name: "Booked by phone → \"You're scheduled, please confirm\" the same day", type: 'Yes/No', value: 'Yes', category: 'Communications', description: 'Goes to tenants who asked for email, with a Confirm button. Everyone else stays on the call list. The Book sheet ticks it for them; the person booking can untick it.' },
  { key: 'EMAIL_ON_CONFIRMATION', name: 'When a visit is confirmed → "See you on [date]"', type: 'Yes/No', value: 'Yes', category: 'Communications', description: 'Queued by the press that records the confirmation, by phone or by the button. Carries a Reschedule button until the cutoff.' },
  { key: 'EMAIL_REMINDER_RULE', name: 'Reminder for anyone still unanswered', type: 'Text', value: 'DAY_BEFORE_0900', category: 'Communications', description: 'DAY_BEFORE_0900, DAY_BEFORE_1600, TWO_DAYS_BEFORE or OFF. Queued by the person who presses "Queue reminder" on Today.' },
  { key: 'TENANT_RESCHEDULE_CUTOFF_HOURS', name: 'Tenants can reschedule from the email until', type: 'Text', value: '24', category: 'Communications', description: 'Hours before the visit (24 or 48) or SAME_DAY_0800. After this the button says "call the office". Cancel stays available until the visit starts.' },
  { key: 'OFFICE_PHONE', name: 'Office phone (the [OfficePhone] token)', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'The number tenants call. Shown on every tenant email and the tenant page.' },
  { key: 'OFFICE_EMAIL', name: 'Office email (the [OfficeEmail] token; the email footer)', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'The address the footer of every automatic email names ("…or email …"). Not set = the footer leaves that part out.' },
  { key: 'EMAIL_INTERNAL_RECIPIENTS', name: 'Internal staff (Compose → Internal)', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'Staff addresses Compose offers as internal recipients, separated by ;.' },
  { key: 'MAIL_GROUP_TO', name: 'Group emails to volunteers: To', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'The Director. Volunteers go in BCC so no address is exposed to another.' },
  { key: 'MAIL_GROUP_CC', name: 'Group emails to volunteers: CC', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'The co-administrator (Caroline).' },
  { key: 'EMAIL_ENTRY_PERMISSION_RECIPIENTS', name: 'Entry permission: who gets a copy', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: "When a tenant agrees to let the team in while they are out, a copy goes here (addresses separated by ;). Not set = the Director (group emails' To) and the building manager." },
  { key: 'CYNTHIA_EMAIL', name: 'Permission to enter by phone: copy to (Cynthia)', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'When the office records a tenant’s permission to enter by phone, the one-page record goes here, with the Director and the office in CC. Not set = the building manager.' },
  { key: 'HEALTH_EMAIL_TO', name: 'Health alert: send to', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'Who gets the email when a check on Admin → Health turns red (and the one that says it is fixed). Not set = the Director (Group emails to volunteers: To). Staff only (1102).' },
  { key: 'EMAIL_CLOSEOUT_REMINDER_MINUTES', name: 'Not closed out → remind the person responsible after', type: 'Text', value: '60', category: 'Communications', description: 'Minutes after a visit’s scheduled end (30, 60, 90, 120) or OFF. One email (Close out now) to the crew, the technology associate or the volunteer, once, never if it is closed; through the Outbox. A volunteer outing counts its scheduled length + 1 hour.' },
  { key: 'EMAIL_READ_REPLIES', name: 'Read replies to the scheduling mailbox', type: 'Yes/No', value: 'No', category: 'Communications', description: 'Yes only after the mailbox read permission is granted (NEEDS-ELI). Replies are filed and shown under Needs your attention; the app never acts on one by itself.' },
  { key: 'COMPANION_TRAINING_DATE', name: 'Next VISIONS training: date ([TrainingDate])', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'Kept for a training invitation the office sends itself. The welcome email no longer names a training or a date (1.8.2).' },
  { key: 'COMPANION_TRAINING_TIME', name: 'Next VISIONS training: time ([TrainingTime])', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'The time of that training.' },
  { key: 'COMPANION_TRAINER_NAME', name: 'Who leads the training ([TrainerName])', type: 'Text', value: NOT_SET_VALUE, category: 'Communications', description: 'Kept for a training invitation the office sends itself (the welcome email names no trainer since 1.8.2).' },
  { key: 'COMPANION_WIDEN_DAYS', name: 'Companion request: days with no answer before "Send to everyone in Companion" is offered', type: 'Text', value: '2', category: 'Communications', description: 'A Companion & Wellness request goes first to the volunteers whose schedule fits (1.7.2). After this many days with no answer (0, 1, 2, 3, 5 or 7) the request offers the second press. Never sent by itself.' },
  { key: 'EVENT_GUESTS_ALLOWED', name: 'Events: guests a tenant may announce on a new event', type: 'Text', value: '2', category: 'Communications', description: '0, 1 or 2. What a new event starts with (1.7.4); each event keeps its own number, changeable on the event page at any time.' },
  { key: 'EVENT_KINDS', name: 'Events: the kinds of event', type: 'Text', value: 'Bingo; Music; Movie night; Tech discussion; Nail spa; Outing; Meeting; Other', category: 'Communications', description: 'The list New event offers under Kind, separated by ; (1.7.7).' },
  { key: 'EVENT_ROOMS', name: 'Events: the rooms of the building', type: 'Text', value: 'Auditorium; Boardroom; Lobby; Community room', category: 'Communications', description: 'The list New event offers under Place, separated by ; — Other is always offered (1.7.7).' },
  { key: 'EVENT_REMINDERS', name: 'Events: reminders', type: 'Yes/No', value: 'Yes', category: 'Communications', description: 'Yes: saving an event schedules its reminders in the Outbox. No cancels every scheduled reminder at once (1.7.7).' },
  { key: 'EVENT_REMINDER_TIMES', name: 'Events: when reminders go', type: 'Text', value: '2@10:00; 0@09:00', category: 'Communications', description: 'Up to three, separated by ; — days before the event @ the time (24-hour, New York). 2@10:00 is two days before at 10:00 AM; 0@09:00 the day of at 9:00 AM.' },
  { key: 'EVENT_REMINDER_AUDIENCE', name: 'Events: who gets reminders', type: 'Text', value: 'ALL', category: 'Communications', description: 'ALL = every tenant with an email · YES = only those who said yes (an event with RSVP) · NOBODY.' },
  { key: 'EMAIL_DISPATCH_LAST_RUN', name: 'Email sender: last run', type: 'Text', value: 'Never', category: 'System', description: 'Written by the dispatcher each run (Health reads it).' },
] as const;

export type EmailSettingKey =
  | (typeof EMAIL_SETTINGS)[number]['key']
  | 'BUILDING_MANAGER_NAME'
  | 'BUILDING_MANAGER_EMAIL'
  | 'EMAIL_SAFETY_RECIPIENTS'
  | 'EMAIL_INCIDENT_RECIPIENTS'
  | 'EMAIL_ENTRY_PERMISSION_RECIPIENTS';

/** Keys an administrator may change from Admin → Settings (the last-run stamp is the dispatcher's). */
export const EDITABLE_EMAIL_SETTINGS = [
  'EMAIL_SEND_DELAY_MINUTES',
  'EMAIL_ON_PHONE_BOOKING',
  'EMAIL_ON_CONFIRMATION',
  'EMAIL_REMINDER_RULE',
  'TENANT_RESCHEDULE_CUTOFF_HOURS',
  'OFFICE_PHONE',
  'OFFICE_EMAIL',
  'EMAIL_INTERNAL_RECIPIENTS',
  'MAIL_GROUP_TO',
  'MAIL_GROUP_CC',
  'COMPANION_TRAINING_DATE',
  'COMPANION_TRAINING_TIME',
  'COMPANION_TRAINER_NAME',
  'COMPANION_WIDEN_DAYS',
  'EVENT_GUESTS_ALLOWED',
  'EVENT_KINDS',
  'EVENT_ROOMS',
  'EVENT_REMINDERS',
  'EVENT_REMINDER_TIMES',
  'EVENT_REMINDER_AUDIENCE',
  'EMAIL_READ_REPLIES',
  'EMAIL_CLOSEOUT_REMINDER_MINUTES',
  'BUILDING_MANAGER_NAME',
  'BUILDING_MANAGER_EMAIL',
  'EMAIL_SAFETY_RECIPIENTS',
  'EMAIL_INCIDENT_RECIPIENTS',
  'EMAIL_ENTRY_PERMISSION_RECIPIENTS',
  'CYNTHIA_EMAIL',
  'HEALTH_EMAIL_TO',
] as const;
export type EditableEmailSetting = (typeof EDITABLE_EMAIL_SETTINGS)[number];

/* ------------------------------------------------------------------------------------------------
 * The office phones (1.4.0): one SM_Settings row, OFFICE_PHONE, holding a list of number + label
 * ("Office: 212-555-0100; After hours: 212-555-0199"). A value written before 1.4.0 is a bare number;
 * it reads as one phone labelled "Office", and the first save writes it in the new form.
 * ---------------------------------------------------------------------------------------------- */

export interface OfficePhone {
  label: string;
  number: string;
}

const PHONE_RE = /^[0-9+().\-\s]{7,25}((x|ext\.?)\s?\d{1,6})?$/i;

export function parseOfficePhones(value: string | null | undefined): OfficePhone[] {
  const v = String(value ?? '').trim();
  if (!v || v === NOT_SET_VALUE) return [];
  return v
    .split(/[;\n]/)
    .map((part) => part.trim())
    .filter(Boolean)
    .map((part, i) => {
      const at = part.lastIndexOf(':');
      if (at < 0) return { label: i === 0 ? 'Office' : `Phone ${i + 1}`, number: part };
      return { label: part.slice(0, at).trim() || 'Office', number: part.slice(at + 1).trim() };
    })
    .filter((p) => p.number);
}

/** The row's value: "Label: number; Label: number". An empty list clears the row. */
export function serializeOfficePhones(list: readonly OfficePhone[]): string {
  const rows = list.map((p) => ({ label: p.label.replace(/[:;\n]/g, ' ').trim(), number: p.number.replace(/[;\n]/g, ' ').trim() })).filter((p) => p.number);
  return rows.length ? rows.map((p) => `${p.label || 'Office'}: ${p.number}`).join('; ') : NOT_SET_VALUE;
}

export function validOfficePhones(value: string): boolean {
  if (value === NOT_SET_VALUE) return true;
  const list = parseOfficePhones(value);
  return value.length <= 400 && list.length > 0 && list.length <= 6 && list.every((p) => PHONE_RE.test(p.number) && p.label.length <= 30);
}

/**
 * What an email or a page prints for [OfficePhone]: one phone labelled "Office" is just the number (the
 * wording reads "call us at 212-555-0100"); several are "212-555-0100 (Office) · 212-555-0199 (After hours)".
 */
export function officePhoneText(value: string | null | undefined): string | null {
  const list = parseOfficePhones(value);
  if (!list.length) return null;
  if (list.length === 1 && /^office$/i.test(list[0]!.label)) return list[0]!.number;
  return list.map((p) => `${p.number} (${p.label})`).join(' · ');
}

export const isYes = (v: string | null | undefined): boolean => /^(yes|true|1|on)$/i.test(String(v ?? '').trim());

/** When a message pressed now may leave: now + the cancel window (or the next 8:00 on the service clock). */
export function sendAfterWindow(delaySetting: string | null | undefined, now: Date): Date {
  const v = String(delaySetting ?? '5').trim().toUpperCase();
  if (v === 'NEXT_MORNING') {
    const todayKey = new Intl.DateTimeFormat('en-CA', { timeZone: SERVICE_TZ, year: 'numeric', month: '2-digit', day: '2-digit' }).format(now).replace(/-/g, '');
    const eightToday = serviceInstant(todayKey, 8 * 60);
    return eightToday.getTime() > now.getTime() ? eightToday : serviceInstant(addDaysToKey(todayKey, 1), 8 * 60);
  }
  const n = Number(v);
  const minutes = Number.isFinite(n) && n >= 0 && n <= 120 ? n : 5;
  return new Date(now.getTime() + minutes * 60_000);
}

/** The window in minutes for display ("5 minutes"); NEXT_MORNING → null. */
export function windowMinutesOf(delaySetting: string | null | undefined): number | null {
  const v = String(delaySetting ?? '5').trim().toUpperCase();
  if (v === 'NEXT_MORNING') return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 && n <= 120 ? n : 5;
}

/** When a reminder for a visit on `dayKey` is set to leave by the rule; null when the rule is OFF. */
export function reminderSendAt(rule: string | null | undefined, dayKey: string): Date | null {
  const r = String(rule ?? 'DAY_BEFORE_0900').trim().toUpperCase();
  if (r === 'OFF') return null;
  if (r === 'DAY_BEFORE_1600') return serviceInstant(addDaysToKey(dayKey, -1), 16 * 60);
  if (r === 'TWO_DAYS_BEFORE') return serviceInstant(addDaysToKey(dayKey, -2), 9 * 60);
  return serviceInstant(addDaysToKey(dayKey, -1), 9 * 60);
}

/** The last moment a tenant may pick a different day from their link, given the visit's start. */
export function rescheduleCutoff(setting: string | null | undefined, dayKey: string, visitStart: Date): Date {
  const v = String(setting ?? '24').trim().toUpperCase();
  if (v === 'SAME_DAY_0800') return serviceInstant(dayKey, 8 * 60);
  const h = Number(v);
  const hours = Number.isFinite(h) && h >= 0 && h <= 24 * 14 ? h : 24;
  return new Date(visitStart.getTime() - hours * 3_600_000);
}

/* ------------------------------------------------------------------------------------------------
 * Templates
 * ---------------------------------------------------------------------------------------------- */

type TV = typeof vocab.SM_EmailTemplates;
export type TemplateProgramGroup = TV['ProgramGroup'][number];
export type TemplateCommType = TV['CommunicationType'][number];
export type TemplateRecipientType = TV['RecipientType'][number];

/** What the app adds to a message when it is sent (never typed into the wording). */
export type LinkKind =
  | 'tenant-confirm'
  | 'tenant-feedback'
  | 'volunteer-availability'
  | 'volunteer-claim'
  | 'volunteer-availability-answer'
  | 'volunteer-signup'
  | 'volunteer-completion'
  | 'volunteer-confirm'
  | 'rsvp'
  | 'entry-permission'
  | 'opt-out'
  /** 1.5.16: the tenant's own page for an agreement sent from their card. */
  | 'tenant-agreement'
  /** 1.7.7: a volunteer's page for the month — every event that needs volunteers, an answer an event. */
  | 'volunteer-month'
  /** 23 Oct 2026 (D-1023-READING): a Reading volunteer's one note box, after the office closed the visit out. */
  | 'volunteer-note';

/** The kinds with a page (rsvp: the event RSVP page, 1.3.0). */
export const LIVE_LINK_KINDS: readonly LinkKind[] = ['tenant-confirm', 'tenant-feedback', 'volunteer-availability', 'volunteer-claim', 'volunteer-availability-answer', 'volunteer-signup', 'volunteer-completion', 'volunteer-confirm', 'rsvp', 'entry-permission', 'opt-out', 'tenant-agreement', 'volunteer-month', 'volunteer-note'];

export interface BuiltInTemplate {
  code: string;
  name: string;
  programGroup: TemplateProgramGroup;
  communicationType: TemplateCommType;
  recipientType: TemplateRecipientType;
  subject: { en: string; es: string };
  /** The email's heading, when the wording has one (stored as the body's first line, `# Heading`). */
  heading: { en: string | null; es: string | null };
  /** The body as stored: `# Heading`, a blank line, then the words. */
  body: { en: string; es: string };
  /** The tenant's own link: Confirm / different day / Cancel buttons are added when it is sent. */
  link?: LinkKind;
  /** Files the dispatcher attaches (from the API's bundled data/attachments). */
  attachments?: string[];
  /** Arrives with a later prompt: listed and editable now, used from then. */
  from?: string;
}

type BuiltInMeta = Omit<BuiltInTemplate, 'subject' | 'heading' | 'body'>;

export const QUICK_START_FILE = 'ABF_Volunteer_Quick_Start.pdf';
/** 13 Oct 2026: the Companion & Wellness Volunteer Handbook — emailed after sign-up to those who chose Companion or Wellness walks. */
export const HANDBOOK_FILE = 'ABF_Volunteer_Handbook.pdf';


/**
 * The app's templates: what each one is for (code, name, the site's choice values, its link and files).
 * Their wording is the Director's, approved 1 Oct 2026 (docs/EMAIL-WORDING-2026-10.md, sections A–D),
 * generated into shared/email-wording.gen.ts; D1–D6 are English only by design, so their Spanish row
 * carries the English. The site's own rows are read as they are; a code here that the site does not have is
 * offered under Admin → Email templates → "Add the missing templates" (a person's press) and, until then, the
 * app renders this wording and says so in the preview.
 */
const BUILT_IN_META: readonly BuiltInMeta[] = [
  {
    code: 'HK_PLEASE_CONFIRM',
    name: "You're scheduled — please confirm",
    programGroup: 'Housekeeping',
    communicationType: 'Appointment Notice',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
  },
  {
    code: 'HK_SEE_YOU',
    name: 'See you on [date]',
    programGroup: 'Housekeeping',
    communicationType: 'Appointment Notice',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
  },
  {
    code: 'HK_REMINDER',
    name: 'Reminder (still unanswered)',
    programGroup: 'Housekeeping',
    communicationType: 'Appointment Reminder',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
  },
  {
    code: 'HK_CANCELLED',
    name: 'Your visit is cancelled',
    programGroup: 'Housekeeping',
    communicationType: 'Tenant Cancellation Confirmation',
    recipientType: 'Tenant',
  },
  {
    code: 'HK_RESCHEDULED',
    name: 'Your visit has a new day',
    programGroup: 'Housekeeping',
    communicationType: 'Rescheduling Notice',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
  },
  {
    code: 'HK_NOTE',
    name: 'A note to a tenant',
    programGroup: 'Housekeeping',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Tenant',
  },
  {
    code: 'INT_HK_INCIDENT',
    name: 'Incident memo (Housekeeping)',
    programGroup: 'Internal Staff',
    communicationType: 'Internal Staff Alert',
    recipientType: 'Internal Staff',
  },
  {
    code: 'INT_UNIT_SAFETY_ALERT',
    name: 'Hazard report to the building manager',
    programGroup: 'Internal Staff',
    communicationType: 'Unit Condition or Safety Alert',
    recipientType: 'Other',
  },
  {
    code: 'INT_STAFF_NOTE',
    name: 'A note to staff',
    programGroup: 'Internal Staff',
    communicationType: 'Internal Staff Alert',
    recipientType: 'Internal Staff',
  },
  {
    code: 'HK_PACKET',
    name: 'Welcome packet (Housekeeping)',
    programGroup: 'Housekeeping',
    communicationType: 'Other',
    recipientType: 'Tenant',
  },
  {
    code: 'DOC_COPY',
    name: 'A copy of a document',
    programGroup: 'All Programs',
    communicationType: 'Other',
    recipientType: 'Tenant',
  },
  /* ---- v4 additions (29 Sep 2026) ---- */
  {
    code: 'TENANT_FEEDBACK',
    name: 'Complaint and Feedback Form',
    programGroup: 'All Programs',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Tenant',
    link: 'tenant-feedback',
    from: '0.8.0',
  },
  {
    code: 'TENANT_PARTNER_NOTICE',
    name: 'You have been partnered with a volunteer',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Appointment Notice',
    recipientType: 'Tenant',
    from: '1.1.0',
  },
  {
    code: 'VOL_WELCOME',
    name: 'Welcome, new volunteer',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Volunteer',
    link: 'volunteer-signup',
    from: '1.1.0',
  },
  {
    code: 'VOL_SIGNED_UP',
    name: "You're signed up (Quick Start attached)",
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Volunteer',
    attachments: [QUICK_START_FILE],
    from: '1.1.0',
  },
  {
    code: 'VOL_QUICK_START',
    name: 'The Volunteer Quick Start',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Volunteer',
    attachments: [QUICK_START_FILE],
    from: '1.1.0',
  },
  {
    code: 'VOL_AVAILABILITY',
    name: 'Set my availability',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Volunteer',
    link: 'volunteer-availability',
    from: '1.1.0',
  },
  {
    code: 'VOL_REQUEST_ONE_TIME',
    name: "A one-time request — I'll take it",
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-claim',
    from: '1.1.0',
  },
  {
    code: 'VOL_REQUEST_CONSISTENT',
    name: 'A regular request — available?',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-availability-answer',
    from: '1.1.0',
  },
  {
    code: 'VOL_CONFIRMATION',
    name: "You're matched",
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    attachments: [QUICK_START_FILE],
    from: '1.1.0',
  },
  {
    code: 'VOL_REMINDER',
    name: 'Volunteer reminder (day before)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Reminder',
    recipientType: 'Volunteer',
    link: 'volunteer-confirm',
    attachments: [QUICK_START_FILE],
    from: '1.1.0',
  },
  {
    code: 'VOL_OUTING_INTEREST',
    name: 'Group outing — interested?',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-claim',
    from: '1.1.0',
  },
  {
    code: 'VOL_OUTING_REMINDER',
    name: 'Group outing reminder',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Reminder',
    recipientType: 'Volunteer',
    attachments: [QUICK_START_FILE],
    from: '1.1.0',
  },
  {
    code: 'VOL_COMPLETION',
    name: 'How did it go? (five questions)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Volunteer',
    link: 'volunteer-completion',
    from: '1.1.0',
  },
  {
    code: 'VOL_CONFIRM_INFO',
    name: 'Confirm your information (go-live)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Volunteer',
    link: 'volunteer-signup',
    from: '1.1.0',
  },
  {
    code: 'INT_COMPANION_INCIDENT',
    name: 'Incident memo (Companion & Wellness, confidential)',
    programGroup: 'Internal Staff',
    communicationType: 'Internal Staff Alert',
    recipientType: 'Internal Staff',
    from: '1.1.0',
  },
  {
    code: 'EVENT_INVITE',
    name: 'Event invitation (RSVP)',
    programGroup: 'All Programs',
    communicationType: 'Other',
    recipientType: 'Tenant',
    link: 'rsvp',
    from: '1.3.0',
  },
  {
    code: 'EVENT_RAFFLE_WINNER',
    name: 'Raffle winner',
    programGroup: 'All Programs',
    communicationType: 'Other',
    recipientType: 'Tenant',
    from: '1.3.0',
  },
  {
    code: 'EVENT_REMINDER',
    name: 'Event reminder',
    programGroup: 'All Programs',
    communicationType: 'Appointment Reminder',
    recipientType: 'Tenant',
    from: '1.3.0',
  },
  {
    code: 'HK_ENTRY_PERMISSION',
    name: 'Permission to enter while the tenant is out',
    programGroup: 'Housekeeping',
    communicationType: 'Appointment Notice',
    recipientType: 'Tenant',
    link: 'entry-permission',
    from: '1.4.0',
  },
  {
    code: 'INT_ENTRY_PERMISSION_COPY',
    name: 'Copy of a tenant’s entry permission',
    programGroup: 'Internal Staff',
    communicationType: 'Internal Staff Alert',
    recipientType: 'Internal Staff',
    from: '1.4.0',
  },
  /* ---- 1.5.0 (section E of the Director's wording) ---- */
  {
    code: 'TECH_TICKET',
    name: 'Tech ticket (to the technology associate)',
    programGroup: 'All Programs',
    communicationType: 'Appointment Notice',
    recipientType: 'Other',
    from: '1.5.0',
  },
  {
    code: 'TECH_BOOKED',
    name: 'Tech Support booked — please confirm',
    programGroup: 'All Programs',
    communicationType: 'Appointment Notice',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
    from: '1.5.0',
  },
  {
    code: 'TECH_REMINDER',
    name: 'Tech Support reminder (the morning of)',
    programGroup: 'All Programs',
    communicationType: 'Appointment Reminder',
    recipientType: 'Tenant',
    link: 'tenant-confirm',
    from: '1.5.0',
  },
  {
    // 23 Oct 2026 (D-1023-READING): sent only on the office's press that a Reading visit was met
    code: 'VOL_READING_THANKS',
    name: 'Thank you for reading with us (after the office closes out a Reading visit)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Volunteer',
    link: 'volunteer-note',
  },
  {
    // 24 Oct 2026 (D-1023-READING, Eli's picks): the office's answer to a reading volunteer's note — typed, queued on a press
    code: 'VOL_READING_REPLY',
    name: "Answer to a reading volunteer's note",
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Volunteer',
  },
  {
    // 24 Oct 2026 (D-1023-READING, Eli's picks): one thank-you to each volunteer who helped at an event, after its date
    code: 'EVENT_HELPER_THANKS',
    name: 'Thank you for helping at an event',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Volunteer',
  },
  {
    code: 'CLOSEOUT_REMINDER',
    name: 'Please close out (one hour after the end)',
    programGroup: 'All Programs',
    communicationType: 'Follow-Up Communication',
    recipientType: 'Other',
    from: '1.5.0',
  },
  {
    code: 'VOL_ACTIVITY_REQUEST',
    name: 'Event & activity support — can you help?',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-claim',
    from: '1.5.1',
  },
  {
    // 1.7.2: not an email of its own — the paragraph the first confirmation carries, with the availability link
    code: 'VOL_SCHEDULE_INVITE',
    name: 'Your usual days and times (the paragraph on a first confirmation)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-availability',
    from: '1.7.2',
  },
  {
    // 1.7.7: Request volunteers — one event, the volunteer's own link, two buttons
    code: 'EVENT_VOLUNTEER_REQUEST',
    name: 'Event: can you help? (Request volunteers)',
    programGroup: 'All Programs',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'rsvp',
    from: '1.7.7',
  },
  {
    // 1.7.7: Request for the month — every event that needs volunteers, one link to the volunteer's own page
    code: 'EVENT_VOLUNTEER_MONTH',
    name: 'Events: the month\'s request to volunteers',
    programGroup: 'All Programs',
    communicationType: 'Volunteer Assignment',
    recipientType: 'Volunteer',
    link: 'volunteer-month',
    from: '1.7.7',
  },
  {
    // 1.7.7: the scheduled reminder as a confirmed volunteer gets it — 1.7.9: with their own link to the answer box
    code: 'EVENT_REMINDER_VOLUNTEER',
    name: 'Event reminder (to a volunteer who is helping)',
    programGroup: 'All Programs',
    communicationType: 'Appointment Reminder',
    recipientType: 'Volunteer',
    link: 'rsvp',
    from: '1.7.7',
  },
  {
    // 1.7.3: a survey's invitation — one button, the tenant's own link (their name and apartment filled in)
    code: 'SURVEY_INVITE',
    name: 'Survey invitation',
    programGroup: 'All Programs',
    communicationType: 'Other',
    recipientType: 'Tenant',
    from: '1.7.3',
  },
  {
    code: 'VISIONS_REPORT',
    name: 'Monthly VISIONS report (to the liaison)',
    programGroup: 'Community Companion and Wellness',
    communicationType: 'Other',
    recipientType: 'Other',
    from: '1.2.0',
  },
];

export const BUILT_IN_TEMPLATES: readonly BuiltInTemplate[] = BUILT_IN_META.map((m) => {
  const w = DIRECTOR_WORDING[m.code];
  if (!w) throw new Error(`No wording for the built-in template ${m.code}.`);
  const es = w.es ?? w.en;
  return {
    ...m,
    subject: { en: w.en.subject, es: es.subject },
    heading: { en: w.en.heading, es: es.heading },
    body: { en: composeBody(w.en.heading, w.en.body), es: composeBody(es.heading, es.body) },
  };
});

/** The internal memos (D1–D6): English only; their footer is the address line alone. */
export const INTERNAL_TEMPLATE_CODES: ReadonlySet<string> = new Set(['INT_HK_INCIDENT', 'INT_UNIT_SAFETY_ALERT', 'INT_STAFF_NOTE', 'INT_ENTRY_PERMISSION_COPY', 'INT_COMPANION_INCIDENT', 'VISIONS_REPORT']);

/** A message to staff (a memo, the office's copy of a group send): its footer is the address line only. */
export function isInternalMessage(templateCode: string | null | undefined): boolean {
  const c = baseCode(String(templateCode ?? '')).replace(/-V\d{8}$/, '');
  return INTERNAL_TEMPLATE_CODES.has(c) || c.startsWith('INT_') || c === 'VOL_GROUP_COPY';
}

export const BUILT_IN_BY_CODE: ReadonlyMap<string, BuiltInTemplate> = new Map(BUILT_IN_TEMPLATES.map((t) => [t.code, t]));

/** The Spanish body lives in a second row `<CODE>_ES` (docs/DECISIONS.md D-0.6.0-2). */
export const ES_SUFFIX = '_ES';
export const esCode = (code: string): string => `${code}${ES_SUFFIX}`;
export const baseCode = (code: string): string => (code.endsWith(ES_SUFFIX) ? code.slice(0, -ES_SUFFIX.length) : code);

/** The Admin → Settings "Wording" picker: which email → template code. */
export const WORDING_PICKER = [
  { key: 'ask', code: 'HK_PLEASE_CONFIRM' },
  { key: 'conf', code: 'HK_SEE_YOU' },
  { key: 'rem', code: 'HK_REMINDER' },
  { key: 'haz', code: 'INT_UNIT_SAFETY_ALERT' },
] as const;

/** The app's own purposes and the template each press uses. */
export const PURPOSE_TEMPLATE = {
  pleaseConfirm: 'HK_PLEASE_CONFIRM',
  seeYou: 'HK_SEE_YOU',
  reminder: 'HK_REMINDER',
  incident: 'INT_HK_INCIDENT',
  safety: 'INT_UNIT_SAFETY_ALERT',
  packet: 'HK_PACKET',
  docCopy: 'DOC_COPY',
} as const;

/** The buttons the app adds to a tenant's email (they open the tenant's page; a GET never acts). Labels: the Director's wording, 1 Oct 2026. */
export const LINK_WORDS = {
  en: {
    confirm: "Yes, I'll be home — confirm",
    pick: 'I need a different day',
    cancel: 'Cancel this visit',
    plainLink: 'Your page',
    feedback: 'Tell us how it went',
  },
  es: {
    confirm: 'Sí, estaré en casa — confirmar',
    pick: 'Necesito otro día',
    cancel: 'Cancelar esta visita',
    plainLink: 'Su página',
    feedback: 'Cuéntenos cómo fue',
  },
} as const;

/** The labels the app adds as buttons (the volunteer's language) — the Director's wording, 1 Oct 2026. */
export const VOL_BUTTONS = {
  en: {
    availability: 'Set my availability',
    signup: 'Complete your registration',
    confirmInfo: 'Confirm my information',
    take: "I'll take it",
    answer: "Yes, I'm available",
    propose: 'Propose another time',
    interested: 'I accept — I will be there',
    confirm: 'Confirm',
    completion: 'Answer the five questions',
    note: 'Leave a note',
    google: 'Walking directions (Google Maps)',
    apple: 'Walking directions (Apple Maps)',
  },
  es: {
    availability: 'Marcar mi disponibilidad',
    signup: 'Completar mi registro',
    confirmInfo: 'Confirmar mis datos',
    take: 'Yo voy',
    answer: 'Sí, estoy disponible',
    propose: 'Proponer otra hora',
    interested: 'Acepto — ahí estaré',
    confirm: 'Confirmar',
    completion: 'Responder las cinco preguntas',
    note: 'Dejar una nota',
    google: 'Cómo llegar a pie (Google Maps)',
    apple: 'Cómo llegar a pie (Apple Maps)',
  },
} as const;

/**
 * The footer on every automatic email (the Director's wording, 1 Oct 2026 — docs/EMAIL-WORDING-2026-10.md):
 * part of the layout, under the signature, not a template row and not editable per template. The internal
 * memos (D1–D6) carry the address line only. With no office email set the "or email …" part is left out;
 * with no office phone, the number is.
 */
export const EMAIL_FOOTER = {
  en: {
    address: 'Associated Blind Foundation · 135 West 23rd Street, New York, NY 10011',
    office: ' · Office: {phone}',
    sent: 'This email was sent automatically by the ABF Scheduler. ',
    strong: "Please don't reply to it — this mailbox is not checked.",
    reach: {
      both: ' To reach us, use the buttons above, call the office at {phone}, or email {email}.',
      phone: ' To reach us, use the buttons above or call the office at {phone}.',
      email: ' To reach us, use the buttons above, call the office, or email {email}.',
      none: ' To reach us, use the buttons above or call the office.',
    },
    /** 1.8.3: an email with no buttons says nothing about buttons. */
    reachNoButtons: {
      both: ' To reach us, call the office at {phone} or email {email}.',
      phone: ' To reach us, call the office at {phone}.',
      email: ' To reach us, call the office or email {email}.',
      none: ' To reach us, call the office.',
    },
    buttons: " The buttons in this email are yours alone — please don't forward it.",
  },
  es: {
    address: 'Associated Blind Foundation · 135 West 23rd Street, New York, NY 10011',
    office: ' · Oficina: {phone}',
    sent: 'Este correo fue enviado automáticamente por el ABF Scheduler. ',
    strong: 'Por favor no responda a este correo: este buzón no se revisa.',
    reach: {
      both: ' Para comunicarse con nosotros, use los botones de arriba, llame a la oficina al {phone} o escriba a {email}.',
      phone: ' Para comunicarse con nosotros, use los botones de arriba o llame a la oficina al {phone}.',
      email: ' Para comunicarse con nosotros, use los botones de arriba, llame a la oficina o escriba a {email}.',
      none: ' Para comunicarse con nosotros, use los botones de arriba o llame a la oficina.',
    },
    reachNoButtons: {
      both: ' Para comunicarse con nosotros, llame a la oficina al {phone} o escriba a {email}.',
      phone: ' Para comunicarse con nosotros, llame a la oficina al {phone}.',
      email: ' Para comunicarse con nosotros, llame a la oficina o escriba a {email}.',
      none: ' Para comunicarse con nosotros, llame a la oficina.',
    },
    buttons: ' Los botones de este correo son solo para usted; por favor no lo reenvíe.',
  },
} as const;

export interface EmailFooter {
  address: string;
  /** The notice in three pieces: before, the bold sentence, after (null for an internal memo). */
  notice: [string, string, string] | null;
  /** 1.5.4: "Don't want to volunteer with ABF any more? Stop these emails" — the question, the link's words, its address. */
  optOut?: { question: string; label: string; url: string } | null;
}

/**
 * 1.5.4: the footer line on every email to a volunteer, and on a tenant's emails that are not about a booked visit
 * (events, invitations). The link is the person's own (`/link/…`, kind opt-out), minted when it is sent.
 */
export const OPT_OUT_LINE = {
  volunteer: {
    en: { question: "Don't want to volunteer with ABF any more?", label: 'Stop these emails' },
    es: { question: '¿Ya no quiere ser voluntario con ABF?', label: 'Dejar de recibir estos correos' },
  },
  tenant: {
    en: { question: "Don't want emails about events and invitations?", label: 'Stop these emails' },
    es: { question: '¿No quiere recibir correos sobre eventos e invitaciones?', label: 'Dejar de recibir estos correos' },
  },
  /** One message to many tenants in BCC (Invite all tenants): no link can be one person's, so the office takes it. */
  tenantGroup: {
    en: { question: "Don't want emails about events and invitations?", label: 'Call the office and say “stop event emails”.' },
    es: { question: '¿No quiere recibir correos sobre eventos e invitaciones?', label: 'Llame a la oficina y diga “no más correos de eventos”.' },
  },
  /** 1.7.6 — an announcement: tenants, volunteers and extra addresses together in BCC. A person takes them off the list. */
  group: {
    en: { question: "Don't want these emails?", label: 'Call the office and say “stop my emails” — a person will take you off the list.' },
    es: { question: '¿No quiere recibir estos correos?', label: 'Llame a la oficina y diga “no más correos” — una persona le quitará de la lista.' },
  },
} as const;

/**
 * 1.5.4: a tenant's emails that are not about a booked visit — the ones "Stop these emails" stops. A visit's own
 * emails (please confirm, see you, reminder, reschedule, cancelled, Tech Support) are never stopped (D-1.5.4-5).
 */
export const TENANT_NOT_VISIT_CODES: readonly string[] = ['EVENT_INVITE', 'EVENT_REMINDER', 'SURVEY_INVITE'];
export const isTenantNotVisitCode = (code: string): boolean => TENANT_NOT_VISIT_CODES.includes(baseCode(code));

/**
 * The footer under every email. 1.8.3 (D-1.8.3-2): `hasButtons` — whether THIS email has buttons (the layout's
 * `buttons.length`). With buttons the footer is as it always was; without, it does not send the reader to buttons
 * that are not there and leaves out "The buttons in this email are yours alone". Every caller says which.
 */
export function emailFooter(lang: Lang, office: { phone: string | null; email: string | null }, internal: boolean, hasButtons: boolean): EmailFooter {
  const f = EMAIL_FOOTER[lang] ?? EMAIL_FOOTER.en;
  const fill = (s: string) => s.replace('{phone}', office.phone ?? '').replace('{email}', office.email ?? '');
  const address = f.address + (office.phone ? fill(f.office) : '');
  if (internal) return { address, notice: null };
  const words = hasButtons ? f.reach : f.reachNoButtons;
  const reach = office.phone && office.email ? words.both : office.phone ? words.phone : office.email ? words.email : words.none;
  return { address, notice: [f.sent, f.strong, fill(reach) + (hasButtons ? f.buttons : '')] };
}

/** The footer as plain text (the email's text part and the previews). */
export function footerText(f: EmailFooter): string {
  return [f.address, f.notice ? f.notice.join('') : '', f.optOut ? `${f.optOut.question} ${f.optOut.label}${f.optOut.url ? `: ${f.optOut.url}` : ''}` : ''].filter(Boolean).join('\n\n');
}

/** Which buttons a tenant-confirm email carries: a confirmed visit needs no Confirm button. */
export function tenantButtons(templateCode: string): ('confirm' | 'pick' | 'cancel')[] {
  return baseCode(templateCode) === 'HK_SEE_YOU' ? ['pick', 'cancel'] : ['confirm', 'pick', 'cancel'];
}

/** The Tech Support emails' own button words (section E — the Director's wording, 1.5.0). */
export const TECH_LINK_WORDS = {
  en: { confirm: 'Yes, that works — confirm', pick: 'I need a different time', cancel: 'Cancel this appointment' },
  es: { confirm: 'Sí, me funciona — confirmar', pick: 'Necesito otra hora', cancel: 'Cancelar esta cita' },
} as const;

/** The words on a tenant-confirm email's buttons, in order (Tech Support has its own). */
export function tenantButtonWords(templateCode: string, lang: Lang): { key: 'confirm' | 'pick' | 'cancel'; label: string }[] {
  const words = baseCode(templateCode).startsWith('TECH_') ? TECH_LINK_WORDS[lang] : LINK_WORDS[lang];
  return tenantButtons(templateCode).map((key) => ({ key, label: words[key] }));
}

/** E5's one button (the volunteer's claim link — used from 1.5.1). */
export const ACTIVITY_HELP_WORDS = { en: 'I can help', es: 'Puedo ayudar' } as const;

/** The buttons the app adds to the other 1.5.0 emails (plain links, not a tenant's link). */
export const TECH_BUTTONS = {
  en: { openTicket: 'Open ticket', callTenant: 'Call the tenant', closeOut: 'Close out now' },
  es: { openTicket: 'Abrir el ticket', callTenant: 'Llamar al inquilino', closeOut: 'Cerrar ahora' },
} as const;

import type { FeedbackPage } from './feedback';
import type { VolunteerLinkPage } from './volunteers-api';
import type { RsvpLinkPage } from './events';
import type { EntryLinkPage } from './arrival';
import type { AgreementLinkPage } from './agreement-cards';
import type { MessageProgram, ProgramCounts } from './message-programs';

/* ------------------------------------------------------------------------------------------------
 * API shapes
 * ---------------------------------------------------------------------------------------------- */

export interface TemplateView {
  code: string;
  name: string;
  programGroup: string;
  communicationType: string;
  recipientType: string;
  subject: string;
  body: string;
  active: boolean;
  version: string;
  /** Where the wording comes from: a row on the site, or the app's own (not on the site yet). */
  source: 'site' | 'app';
  supersedes: string | null;
  /** The Spanish row (`<CODE>_ES`), when there is one. */
  es: { subject: string; body: string; active: boolean; version: string; source: 'site' | 'app' } | null;
  link: LinkKind | null;
  from: string | null;
  tokens: string[];
  unknownTokens: string[];
}

export interface TemplatesResponse {
  templates: TemplateView[];
  /** Tokens the site's bodies use that the renderer does not know (they render as [?Token]). */
  foundTokens: { token: string; supported: boolean; in: string[] }[];
  missingOnSite: string[];
}

export type MessageTab = 'queued' | 'sent' | 'failed' | 'cancelled' | 'drafts';

export interface MessageView {
  id: number;
  communicationId: string;
  status: string;
  delivery: string;
  channel: string;
  direction: string;
  subject: string;
  summary: string;
  to: string;
  recipientName: string;
  recipientType: string;
  templateCode: string | null;
  tenantId: string | null;
  appointmentId: string | null;
  scheduledAt: string | null;
  sentAt: string | null;
  failedAt: string | null;
  failureReason: string | null;
  retryCount: number;
  by: string | null;
  cancelledBy: string | null;
  createdAt: string | null;
  canCancel: boolean;
  /** Inbound: a tenant's link answer or a reply that waits for a person. */
  followUp: boolean;
  response: string | null;
  /** 1.7.7: an event's reminder, armed when the event was saved and waiting for its time (the Outbox says "Scheduled"). */
  scheduled?: boolean;
  /** 1.7.9: this row's wording was changed in the Outbox — by whom (the first name from the address) and when. */
  edited?: { by: string; name: string; at: string } | null;
  /** 24 Oct 2026 (D-1024-MESSAGES): the program this message belongs to (its template's; Office when unknown). Set by the list read. */
  program?: MessageProgram;
}

export interface MessagesResponse {
  messages: MessageView[];
  counts: Record<MessageTab, number>;
  /** 24 Oct 2026: how many messages of THIS tab each program has (after the search, before the program filter). */
  programCounts?: ProgramCounts;
  now: string;
}

export interface ComposeRecipient {
  kind: 'tenant' | 'internal' | 'free';
  address: string;
  name: string;
  /** Tenants: false when EmailCommunicationAllowed is not ticked (Compose shows why and does not offer them). */
  allowed: boolean;
}

export interface ComposeContext {
  tenant: { tenantId: string; name: string; unit: string; email: string | null; emailAllowed: boolean; lang: Lang } | null;
  appointment: { appointmentId: string; dayKey: string; period: string; status: string } | null;
  recipients: ComposeRecipient[];
  templates: { code: string; name: string; recipientType: string; link: LinkKind | null; from: string | null }[];
  windowMinutes: number | null;
}

export interface PreviewResponse {
  subject: string;
  body: string;
  lang: Lang;
  /** D-1028-ONELANG: Spanish was asked for but this template has no Spanish text, so the English is what goes. */
  noSpanishText?: boolean;
  /** D-1028-ONELANG-8: Spanish was asked for, the message has English words but no Spanish wording, so the whole email is English. */
  noSpanishWording?: boolean;
  unknown: string[];
  missing: string[];
  buttons: string[];
  templateSource: 'site' | 'app';
  templateVersion: string;
  sendAt: string;
  blocked: string | null;
}

export interface EmailSettingsView {
  values: Record<string, string>;
  created: string[];
  automaticEmailEnabled: string;
}

/* ------------------------------------------------------------------------------------------------
 * The link pages (/link/:token and /t/:token) — public, no sign-in
 * ---------------------------------------------------------------------------------------------- */

export type LinkState = 'ok' | 'not-valid' | 'expired' | 'replaced' | 'coming-soon' | 'unavailable' | 'answered';

/**
 * The tenant confirm page. Only the visit's own day, period and unit — never a name, a phone, another
 * person or anything else (prompt 0.6.0 item 7).
 */
export interface TenantConfirmPage {
  kind: 'tenant-confirm';
  state: LinkState;
  lang: Lang;
  officePhone: string | null;
  visit: {
    dayKey: string;
    day: string;
    period: string;
    /** 1.5.2: the arrival window the tenant is told ("between 10:30 and 12:00"), never the exact time. */
    window?: string | null;
    unit: string;
    /** scheduled (waiting for an answer) · confirmed · cancelled · moved · started · done */
    status: 'scheduled' | 'confirmed' | 'cancelled' | 'moved' | 'started' | 'done';
    movedTo: { day: string; period: string } | null;
  } | null;
  canConfirm: boolean;
  canPick: boolean;
  canCancel: boolean;
  /** After the crew's end of day, awaiting the Director's approval (1.5.0): no Confirm until approved. */
  awaitingApproval?: boolean;
  /** A Tech Support appointment (1.5.0): "I need a different time" asks the tenant to call the office. */
  tech?: boolean;
  /** Its time and place, in words ("10:00 · the Boardroom"). */
  techWhen?: string | null;
  /** Past TENANT_RESCHEDULE_CUTOFF_HOURS: the different-day button leads to "call the office". */
  pastCutoff: boolean;
  /** An answer from this link still with the processor. */
  pending: { txId: string; action: 'confirm' | 'reschedule' | 'cancel' } | null;
  /**
   * 1.5.3: a Tech Support appointment whose tenant has no agreement on file for the current version — the page shows
   * the full text above Confirm (shared/agreements.ts) and Confirm waits for the box. Absent when nothing is asked.
   */
  agreement?: { version: string; sha256: string; /** Counsel-approved Spanish text (setting TECH_RELEASE_TEXT_ES), or null. */ textEs: string | null } | null;
}

export interface LinkViewResponse {
  kind: LinkKind | null;
  state: LinkState;
  lang: Lang;
  page: TenantConfirmPage | null;
  /** tenant-feedback links (0.8.0): the Complaint and Feedback Form's page. */
  feedback?: FeedbackPage | null;
  /** The volunteer's own links (1.1.0). */
  volunteer?: VolunteerLinkPage | null;
  /** The event RSVP page (1.3.0). */
  rsvp?: RsvpLinkPage | null;
  /** Permission to enter while the tenant is out (1.4.0). */
  entry?: EntryLinkPage | null;
  /** 1.5.4: "Stop these emails" — a volunteer's or a tenant's own opt-out page. */
  optOut?: OptOutPage | null;
  /** 1.5.16: an agreement sent from the tenant's card — their own page to read and sign it. */
  agreement?: AgreementLinkPage | null;
  /** 1.7.7: a volunteer's month — every event that needs volunteers, an answer an event. */
  volunteerMonth?: import('./event-calendar').VolunteerMonthPage | null;
}

/** 1.5.4: the opt-out page — first name only, what will happen, two buttons. */
export interface OptOutPage {
  who: 'volunteer' | 'tenant';
  firstName: string;
  lang: Lang;
  /** Where they stand now: 'out' after Stop (the page says so, and offers Keep me on the list). */
  state: 'in' | 'out';
  since: string | null;
  officePhone: string | null;
}

export interface LinkDaysResponse {
  days: { dayKey: string; day: string }[];
  /** Closed days in the same weeks (1.5.0): never offered, shown as closed with the reason. */
  closed?: { dayKey: string; day: string; reason: string }[];
}

export interface LinkActionResponse {
  ok: boolean;
  txId: string | null;
  /** Plain words for the page (the processor's own message comes with the status). */
  message: string;
  state?: LinkState;
  /** 1.5.4: the opt-out page's outcome (the page shows its own words for it). */
  result?: 'stopped' | 'already-stopped' | 'kept' | 'back';
}

export interface LinkStatusResponse {
  open: boolean;
  outcome: string | null;
  /** The processor's words, verbatim. */
  outcomeMessage: string | null;
  applied: boolean;
  page: TenantConfirmPage | null;
}
